import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { injectIntl } from 'react-intl';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Capacitor } from '@capacitor/core';
import { isExternalDrop, presentDroppedShare, shareFromDataTransfer } from '../../utils/native/incoming-share-drop';
import './IncomingShareDropLayer.scss';

type IncomingShareDropLayerProps = {
  intl: {
    formatMessage: (descriptor: { id: string }) => string;
  };
  /** True on the signed-in web /home screen. */
  enabled?: boolean;
};

const HIDE_AFTER_MS = 150;
const EXIT_MS = 200;

type DropPhase = 'closed' | 'enter' | 'open' | 'exit';

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const IncomingShareDropLayer = ({ intl, enabled = false }: IncomingShareDropLayerProps) => {
  const active = Boolean(enabled) && !Capacitor.isNativePlatform();
  const [phase, setPhase] = useState<DropPhase>('closed');
  const [hot, setHot] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);
  const phaseRef = useRef<DropPhase>('closed');

  useEffect(() => {
    if (!active) {
      phaseRef.current = 'closed';
      setPhase('closed');
      setHot(false);
      return undefined;
    }

    let hideTimer = 0;
    let openTimer = 0;
    let exitTimer = 0;
    let overTarget = false;

    const moveTo = (next: DropPhase) => {
      phaseRef.current = next;
      setPhase(next);
    };

    const finishExit = () => {
      if (phaseRef.current !== 'exit') return;
      moveTo('closed');
    };

    const show = (nextHot: boolean) => {
      window.clearTimeout(exitTimer);
      if (phaseRef.current === 'closed' || phaseRef.current === 'exit') {
        window.clearTimeout(openTimer);
        if (prefersReducedMotion()) {
          moveTo('open');
        } else {
          moveTo('enter');
          openTimer = window.setTimeout(() => {
            if (phaseRef.current !== 'enter') return;
            moveTo('open');
          }, 16);
        }
      }
      if (overTarget !== nextHot) {
        overTarget = nextHot;
        setHot(nextHot);
      }
    };

    const hide = () => {
      if (phaseRef.current === 'closed' || phaseRef.current === 'exit') return;
      window.clearTimeout(openTimer);
      overTarget = false;
      setHot(false);
      if (prefersReducedMotion()) {
        moveTo('closed');
        return;
      }
      moveTo('exit');
      exitTimer = window.setTimeout(finishExit, EXIT_MS);
    };

    // dragover keeps firing while the pointer is over the window. When it stops, the drag has left.
    const transferOf = (event: Event) => (event as globalThis.DragEvent).dataTransfer;

    const onDragOver = (event: Event) => {
      const transfer = transferOf(event);
      if (!isExternalDrop(transfer ? transfer.types : null)) return;
      event.preventDefault();
      if (transfer) transfer.dropEffect = 'copy';
      const box = targetRef.current;
      show(!!box && event.target instanceof Node && box.contains(event.target));
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(hide, HIDE_AFTER_MS);
    };

    const onDrop = (event: Event) => {
      const transfer = transferOf(event);
      if (!isExternalDrop(transfer ? transfer.types : null)) return;
      event.preventDefault();
      window.clearTimeout(hideTimer);
      const box = targetRef.current;
      const inside = !!box && event.target instanceof Node && box.contains(event.target);
      hide();
      if (!inside || !transfer) return;
      const share = shareFromDataTransfer(transfer);
      if (share) presentDroppedShare(share);
    };

    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(openTimer);
      window.clearTimeout(exitTimer);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      phaseRef.current = 'closed';
      setPhase('closed');
      setHot(false);
    };
  }, [active]);

  if (phase === 'closed') return null;

  const title = intl.formatMessage({ id: 'incoming-share-drop-title' });
  const phaseClass = phase === 'open' ? ' is-open' : phase === 'exit' ? ' is-exit' : ' is-enter';

  return ReactDOM.createPortal(
    <div
      className={`incoming-share-drop${phaseClass}`}
      role="dialog"
      aria-modal="false"
      aria-hidden={phase === 'exit' ? true : undefined}
      aria-label={title}
    >
      <div
        ref={targetRef}
        className={`incoming-share-drop__target${hot ? ' is-hot' : ''}`}
        data-testid="incoming-share-drop-target"
      >
        <UploadFileIcon className="incoming-share-drop__icon" aria-hidden="true" />
        <p id="incoming-share-drop-title" className="incoming-share-drop__title">
          {title}
        </p>
        <p className="incoming-share-drop__hint">{intl.formatMessage({ id: 'incoming-share-drop-hint' })}</p>
      </div>
    </div>,
    document.body
  );
};

export default injectIntl(IncomingShareDropLayer);
